import { ArrowRight, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CustomDrawer from "@/shared/components/ui/Drawer/CustomDrawer";
import Badge from "@/shared/components/ui/Badge/Badge";
import TextButton from "@/shared/components/ui/Button/TextButton";
import { APP_VERSION, formatUpdateDate } from "@/shared/utils/appVersion";

export const CATEGORIES = {
  new: { label: "New feature", variant: "secondary" },
  improvement: { label: "Improvement", variant: "default" },
  fix: { label: "Fix", variant: "success" },
};

function UpdateCard({ update, isNew, onNavigate }) {
  const category = CATEGORIES[update.category] || CATEGORIES.improvement;
  return (
    <article className="flex flex-col gap-2 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="flex items-center gap-2">
        <Badge variant={category.variant} size="xs">{category.label}</Badge>
        <span className="text-xs text-gray-500 dark:text-gray-400">{formatUpdateDate(update.date)}</span>
        {isNew && (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-secondary">
            <span className="h-1.5 w-1.5 rounded-full bg-secondary" aria-hidden="true" />
            New
          </span>
        )}
      </div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{update.title}</h3>
      <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">{update.body}</p>
      {update.cta?.to && (
        <TextButton onClick={() => onNavigate(update.cta.to)} className="-ml-1.5 self-start">
          <span className="inline-flex items-center gap-1">
            {update.cta.label || "Try it"} <ArrowRight size={12} />
          </span>
        </TextButton>
      )}
    </article>
  );
}

/** The product updates feed: a panel on the right, newest first. */
export default function WhatsNewPanel({ isOpen, updates, newIds, onClose }) {
  const navigate = useNavigate();
  const goTo = (to) => {
    onClose();
    navigate(to);
  };
  return (
    <CustomDrawer isOpen={isOpen} onClose={onClose} width={400} drawerClassName="bg-gray-50 dark:bg-gray-950">
      <div className="flex h-full flex-col" role="dialog" aria-modal="true" aria-labelledby="whats-new-title">
        <header className="flex items-start gap-3 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 px-5 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
            <Sparkles size={18} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <h2 id="whats-new-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">What's new</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">The latest features and improvements in SalesWorx</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close what's new"
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <X size={18} />
          </button>
        </header>
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
          {updates.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">No updates yet.</p>
          ) : (
            updates.map((update) => (
              <UpdateCard key={update.id} update={update} isNew={newIds.includes(update.id)} onNavigate={goTo} />
            ))
          )}
        </div>
        <footer className="border-t border-gray-100 dark:border-gray-800 px-5 py-3 text-center text-[11px] text-gray-400">
          You're on version {APP_VERSION}
        </footer>
      </div>
    </CustomDrawer>
  );
}
